import type { SupabaseClient } from '@supabase/supabase-js';
import { parseRecoveryCallback } from './nativeRecoveryContinuation';
import type { CapturedRecoveryRoute } from './recoveryHandoff';

// Proof that a genuine Supabase PASSWORD_RECOVERY exchange happened on this
// client. Held only in module memory — never persisted, never logged.
export interface RecoveryProof {
  userId: string;
  accessToken: string;
}

type Listener = (proof: RecoveryProof | null) => void;

let proof: RecoveryProof | null = null;
const listeners = new Set<Listener>();

export const getRecoveryProof = (): RecoveryProof | null => proof;

export const subscribeRecoveryProof = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const publish = (next: RecoveryProof | null) => {
  proof = next;
  listeners.forEach((l) => l(next));
};

// --- JWT claim classifier (private, synchronous) --------------------------
// Mirrors the backend's OTP-family recovery methods — version-matched to
// deployed Auth v2.197.0: IsRecovery accepts exactly otp, magiclink,
// recovery (internal/models/factor.go). amr entries must be objects; string
// claims are rejected. This screens transport only — JWT signature and
// identity are always proven by provider calls (getUser in the page /
// explicit import path), never by this decode.

const OTP_METHODS = new Set(['otp', 'magiclink', 'recovery']);

const decodeJwtPayload = (jwt: string): Record<string, any> | null => {
  const parts = jwt.split('.');
  if (parts.length !== 3) return null;
  try {
    return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
};

interface OtpClaims { sub: string; sessionId: string }

const otpFamilyClaims = (jwt: string | undefined): OtpClaims | null => {
  const claims = jwt ? decodeJwtPayload(jwt) : null;
  const amr: unknown = claims?.amr;
  if (
    !claims ||
    !Number.isFinite(claims.exp) || claims.exp * 1000 <= Date.now() ||
    typeof claims.sub !== 'string' || !claims.sub ||
    typeof claims.session_id !== 'string' || !claims.session_id ||
    !Array.isArray(amr) || amr.length === 0 ||
    !amr.every((m: any) => m && typeof m === 'object') ||
    !amr.some((m: any) => OTP_METHODS.has(m.method))
  ) {
    return null;
  }
  return { sub: claims.sub, sessionId: claims.session_id };
};

/**
 * Install immediately after createClient so the PASSWORD_RECOVERY event is
 * captured before any URL processing can complete — the async notification is
 * otherwise lost. The callback must stay synchronous: awaiting SDK calls
 * inside onAuthStateChange deadlocks the auth client.
 */
export function installRecoveryTracker(client: SupabaseClient): void {
  client.auth.onAuthStateChange((event, session) => {
    if (event === 'PASSWORD_RECOVERY') {
      // Proof requires a backend-recovery-shaped JWT, not just the event
      // name — an ordinary password session carrying a URL marker cannot
      // publish. Signature is still proven later by provider getUser.
      const c = otpFamilyClaims(session?.access_token);
      publish(
        c && session?.user?.id === c.sub
          ? { userId: c.sub, accessToken: session!.access_token }
          : null
      );
      return;
    }
    if (event === 'SIGNED_OUT' || event === 'SIGNED_IN') {
      // Any sign-in — even the same user via ordinary credentials — ends the
      // recovery flow; the proof must never outlive it.
      publish(null);
      return;
    }
    if (event === 'TOKEN_REFRESHED') {
      // Refresh carries proof only for the same logical session: same user,
      // same session_id, still-valid recovery claims. Anything else clears.
      const next = otpFamilyClaims(session?.access_token);
      const prior = proof ? otpFamilyClaims(proof.accessToken) : null;
      if (
        proof && next && prior &&
        session?.user?.id === proof.userId &&
        next.sub === prior.sub &&
        next.sessionId === prior.sessionId
      ) {
        publish({ userId: proof.userId, accessToken: session!.access_token });
      } else if (proof) {
        publish(null);
      }
    }
  });
}

// --- Explicit browser continuation -------------------------------------------
// The recovery contract is browser-first: any visitor holding a valid reset
// link may finish here — the app is an optional handoff, never a requirement.
// Consumption is explicit opt-in only (the page's "Continue in this browser"
// action); nothing authenticates merely by loading the route.

// Monotonic guard for the multi-step import; any competing continuation or
// auth event invalidates pending work.
let importGeneration = 0;

/**
 * Token-hash path (`?token=<hash>&type=recovery`). verifyOtp is the portable
 * consumer — it needs no PKCE verifier, so a link opened on any browser or
 * device completes here. A valid exchange emits PASSWORD_RECOVERY, which the
 * installed tracker claim-screens and publishes as proof; no proof means the
 * token was rejected (expired, used, or malformed). Returns false on every
 * rejection — no credential or failure detail is logged.
 */
export const continueTokenRecovery = async (
  client: SupabaseClient,
  route: CapturedRecoveryRoute
): Promise<boolean> => {
  const parsed = parseRecoveryCallback(route);
  if (parsed.status !== 'ready' || parsed.kind !== 'tokenHash') return false;
  const myGen = ++importGeneration;
  const stale = () => myGen !== importGeneration;
  try {
    const { error } = await client.auth.verifyOtp({
      token_hash: parsed.credentials.token!,
      type: 'recovery',
    });
    if (stale() || error) return false;
    // Subscribers run before the await resolves, so a published proof is
    // already claim-screened; absence means the tracker rejected it.
    return getRecoveryProof() !== null;
  } catch {
    return false;
  }
};

/**
 * Implicit path (`#access_token=…&refresh_token=…&type=recovery`). The
 * incoming JWT is screened entirely in private memory before any provider
 * call; setSession happens on the ephemeral route client only after provider
 * identity checks pass. Returns false on every rejection — callers see no
 * detail beyond the boolean, no credential is logged.
 */
export const continueImplicitRecovery = async (
  client: SupabaseClient,
  route: CapturedRecoveryRoute
): Promise<boolean> => {
  const parsed = parseRecoveryCallback(route);
  if (parsed.status !== 'ready' || parsed.kind !== 'implicit') return false;
  const accessToken = parsed.credentials.accessToken!;
  const refreshToken = parsed.credentials.refreshToken!;

  const claims = otpFamilyClaims(accessToken);
  if (!claims) return false;

  const myGen = ++importGeneration;
  const stale = () => myGen !== importGeneration;
  // Any competing auth change except our own exact SIGNED_IN echo cancels.
  const { data: watch } = client.auth.onAuthStateChange((event, session) => {
    if (
      event === 'SIGNED_OUT' ||
      (event === 'SIGNED_IN' && session?.access_token !== accessToken)
    ) {
      importGeneration += 1;
    }
  });
  try {
    const { data: userData, error: userError } = await client.auth.getUser(accessToken);
    if (stale()) return false;
    const user = userData?.user;
    if (userError || !user || user.id !== claims.sub) {
      return false;
    }

    const { error: setError } = await client.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (stale() || setError) return false;

    const { data: cur, error: curError } = await client.auth.getSession();
    // The current-client session is the only acceptable proof source — a
    // null/error read or a fallback to the setSession echo proves nothing.
    if (stale() || curError || !cur?.session) return false;
    const session = cur.session;
    if (session.user?.id !== claims.sub || session.access_token !== accessToken) {
      return false;
    }
    // Rechecks expiry + recovery claims after the awaits — a token that
    // lapsed or was swapped mid-flight must not publish proof.
    const sessionClaims = otpFamilyClaims(session.access_token);
    if (
      !sessionClaims ||
      sessionClaims.sub !== claims.sub ||
      sessionClaims.sessionId !== claims.sessionId
    ) {
      return false;
    }

    publish({ userId: claims.sub, accessToken });
    return true;
  } catch {
    return false;
  } finally {
    try {
      watch?.subscription?.unsubscribe();
    } catch {
      /* teardown */
    }
  }
};

/**
 * Browser-facing dispatcher over the consumable shapes. PKCE `code` links are
 * 'unusable' here by construction: the verifier lives only in the client that
 * requested the reset (this client is implicit-flow and never stores one), so
 * a browser can never exchange a code — that shape is app-bound.
 */
export type BrowserRecoveryResult = 'imported' | 'rejected' | 'unusable';

export const continueBrowserRecovery = async (
  client: SupabaseClient,
  route: CapturedRecoveryRoute
): Promise<BrowserRecoveryResult> => {
  const parsed = parseRecoveryCallback(route);
  if (parsed.status !== 'ready') return 'unusable';
  if (parsed.kind === 'pkce') return 'unusable';
  const ok = parsed.kind === 'tokenHash'
    ? await continueTokenRecovery(client, route)
    : await continueImplicitRecovery(client, route);
  return ok ? 'imported' : 'rejected';
};

// External cancellation (route leave / competing flow) — any in-flight
// continuation stops being able to publish.
export const cancelRecoveryImport = (): void => {
  importGeneration += 1;
  publish(null);
};
